import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  CheckCircle2, Clock, AlertTriangle, XCircle, EyeOff, Save, Send, LogOut, ExternalLink,
  MailWarning, Plus, Pencil, Trash2, Building2, Eye, MousePointerClick, Inbox, Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: { l: "Draft", ic: Clock, cls: "bg-kid-mist text-kid-muted" },
  submitted: { l: "In review", ic: Clock, cls: "bg-kid-sun/20 text-[#9a7b1e]" },
  needs_changes: { l: "Changes requested", ic: AlertTriangle, cls: "bg-kid-coral/12 text-kid-coral" },
  approved: { l: "Live", ic: CheckCircle2, cls: "bg-kid-green/15 text-[#3f9d5c]" },
  rejected: { l: "Not approved", ic: XCircle, cls: "bg-kid-coral/12 text-kid-coral" },
  suspended: { l: "Suspended", ic: EyeOff, cls: "bg-kid-coral/12 text-kid-coral" },
};
const ARR = (x) => (Array.isArray(x) ? x : []);
const DELIVERY_FLAGS = [
  ["virtual", "Virtual sessions"], ["in_person", "In-person sessions"], ["hybrid", "Hybrid"],
  ["home_visits", "Home visits"], ["school_visits", "School visits"], ["ontario_wide_virtual", "Virtual across Ontario"],
];

function Chips({ options, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.v;
        const l = typeof o === "string" ? o : o.l;
        const on = ARR(selected).includes(v);
        return (
          <button type="button" key={v} onClick={() => onToggle(v)}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-kid-teal text-white border-kid-teal" : "bg-white text-kid-muted border-kid-line hover:bg-kid-mist"}`}>
            {typeof l === "string" ? l.replace(/-/g, " ") : l}
          </button>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.draft;
  const Ic = s.ic;
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}><Ic size={12} /> {s.l}</span>;
}

// -------- Organization panel --------
function OrgPanel({ meta, org, onSaved }) {
  const [f, setF] = useState({
    name: org.name || "", contact_name: org.contact_name || "", provider_type: org.provider_type || "individual",
    website_url: org.website_url || "", public_email: org.public_email || "", public_phone: org.public_phone || "",
    about: org.about || "", logo_url: org.logo_url || "", accessibility_notes: org.accessibility_notes || "",
    languages: ARR(org.languages).join(", "), cities: ARR(org.cities), regions: ARR(org.regions),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, v) => setF((s) => ({ ...s, [k]: ARR(s[k]).includes(v) ? s[k].filter((x) => x !== v) : [...ARR(s[k]), v] }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/marketplace/provider/me/org", {
        name: f.name, contact_name: f.contact_name, provider_type: f.provider_type,
        website_url: f.website_url, public_email: f.public_email, public_phone: f.public_phone,
        about: f.about, logo_url: f.logo_url, accessibility_notes: f.accessibility_notes,
        languages: f.languages.split(",").map((s) => s.trim()).filter(Boolean),
        cities: f.cities, regions: f.regions,
      });
      toast.success("Organization details saved."); onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="kid-card p-6 mb-8" data-testid="org-panel">
      <div className="flex items-center gap-2 mb-4">
        <Building2 size={18} className="text-kid-teal" />
        <h2 className="font-display font-bold text-kid-navy text-lg">Organization details</h2>
        <span className="text-xs text-kid-muted">— shared across all your service listings</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-sm">Organization / practice name *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-white" data-testid="org-name" /></div>
        <div><Label className="text-sm">Contact name</Label><Input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-1.5 bg-white" data-testid="org-contact" /></div>
        <div><Label className="text-sm">Provider type</Label>
          <Select value={f.provider_type} onValueChange={(v) => set("provider_type", v)}>
            <SelectTrigger className="mt-1.5 bg-white" data-testid="org-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="individual">Individual practitioner</SelectItem><SelectItem value="clinic">Clinic</SelectItem><SelectItem value="agency">Agency</SelectItem><SelectItem value="nonprofit">Non-profit</SelectItem><SelectItem value="program">Program</SelectItem></SelectContent>
          </Select></div>
        <div><Label className="text-sm">Website</Label><Input value={f.website_url} onChange={(e) => set("website_url", e.target.value)} placeholder="https://" className="mt-1.5 bg-white" data-testid="org-website" /></div>
        <div><Label className="text-sm">Public email</Label><Input value={f.public_email} onChange={(e) => set("public_email", e.target.value)} className="mt-1.5 bg-white" data-testid="org-email" /></div>
        <div><Label className="text-sm">Public phone</Label><Input value={f.public_phone} onChange={(e) => set("public_phone", e.target.value)} className="mt-1.5 bg-white" data-testid="org-phone" /></div>
        <div className="sm:col-span-2"><Label className="text-sm">Logo URL</Label><Input value={f.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://" className="mt-1.5 bg-white" data-testid="org-logo" /></div>
        <div className="sm:col-span-2"><Label className="text-sm">About your organization</Label><Textarea value={f.about} onChange={(e) => set("about", e.target.value)} rows={3} className="mt-1.5 bg-white" data-testid="org-about" /></div>
        <div className="sm:col-span-2"><Label className="text-sm">Languages <span className="text-kid-muted">(comma separated)</span></Label><Input value={f.languages} onChange={(e) => set("languages", e.target.value)} placeholder="English, French" className="mt-1.5 bg-white" data-testid="org-languages" /></div>
      </div>
      <Label className="text-sm block mt-4 mb-2">Cities served</Label>
      <div className="max-h-40 overflow-y-auto border border-kid-line rounded-xl p-3">
        <Chips options={meta.cities} selected={f.cities} onToggle={(v) => toggleArr("cities", v)} testid="org-cities" />
      </div>
      <div className="mt-4"><Label className="text-sm">Accessibility notes</Label><Input value={f.accessibility_notes} onChange={(e) => set("accessibility_notes", e.target.value)} className="mt-1.5 bg-white" data-testid="org-accessibility" /></div>
      <div className="mt-5"><Button onClick={save} disabled={saving} data-testid="org-save"><Save size={16} className="mr-1.5" /> Save organization</Button></div>
    </div>
  );
}

// -------- Listing editor (sheet) --------
function ListingEditor({ meta, listing, open, onClose, onChanged }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!listing) return;
    setF({
      headline: listing.headline || "", short_bio: listing.short_bio || "", detailed_bio: listing.detailed_bio || "",
      approach: listing.approach || "", what_to_expect: listing.what_to_expect || "", service_description: listing.service_description || "",
      primary_category: listing.primary_category || "", accepting_new_clients: listing.accepting_new_clients || "varies",
      categories: ARR(listing.categories), age_ranges: ARR(listing.age_ranges), support_areas: ARR(listing.support_areas),
      delivery: listing.delivery || {}, credentials: listing.credentials || {}, booking: listing.booking || {},
      attestation: !!listing.attestation,
    });
  }, [listing]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, v) => setF((s) => ({ ...s, [k]: ARR(s[k]).includes(v) ? s[k].filter((x) => x !== v) : [...ARR(s[k]), v] }));
  const setDelivery = (k, v) => setF((s) => ({ ...s, delivery: { ...s.delivery, [k]: v } }));
  const setCred = (k, v) => setF((s) => ({ ...s, credentials: { ...s.credentials, [k]: v } }));
  const setBooking = (k, v) => setF((s) => ({ ...s, booking: { ...s.booking, [k]: v } }));

  const payload = () => ({
    headline: f.headline, short_bio: f.short_bio, detailed_bio: f.detailed_bio, approach: f.approach,
    what_to_expect: f.what_to_expect, service_description: f.service_description, primary_category: f.primary_category,
    accepting_new_clients: f.accepting_new_clients, categories: f.categories, age_ranges: f.age_ranges,
    support_areas: f.support_areas, delivery: f.delivery, credentials: f.credentials, booking: f.booking, attestation: f.attestation,
  });

  const save = async () => {
    setSaving(true);
    try { await api.patch(`/marketplace/provider/me/listings/${listing.id}`, payload()); toast.success("Listing saved."); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/marketplace/provider/me/listings/${listing.id}`, payload());
      await api.post(`/marketplace/provider/me/listings/${listing.id}/submit`);
      toast.success("Submitted for review!"); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!listing) return null;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="listing-editor">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">Edit service listing <StatusBadge status={listing.profile_status} /></SheetTitle>
          <SheetDescription>These details are specific to this service. Centre-wide info lives in Organization details.</SheetDescription>
        </SheetHeader>
        {listing.admin_notes && ["needs_changes", "rejected"].includes(listing.profile_status) && (
          <div className="mt-4 rounded-xl bg-kid-mist p-3 text-sm text-kid-navy" data-testid="listing-admin-notes"><b>Reviewer notes:</b> {listing.admin_notes}</div>
        )}
        <div className="mt-5 space-y-6">
          <div>
            <Label className="text-sm">Primary service *</Label>
            <Select value={f.primary_category} onValueChange={(v) => { set("primary_category", v); if (!ARR(f.categories).includes(v)) toggleArr("categories", v); }}>
              <SelectTrigger className="mt-1.5 bg-white" data-testid="le-primary"><SelectValue placeholder="Choose primary service" /></SelectTrigger>
              <SelectContent>{meta.categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Label className="text-sm block mt-4 mb-2">Related services offered</Label>
            <Chips options={meta.categories.map((c) => ({ v: c.slug, l: c.name }))} selected={f.categories} onToggle={(v) => toggleArr("categories", v)} testid="le-categories" />
          </div>
          <div><Label className="text-sm">Headline * <span className="text-kid-muted">(one line families see first)</span></Label><Input value={f.headline} onChange={(e) => set("headline", e.target.value)} placeholder="Play-based speech therapy for autistic kids" className="mt-1.5 bg-white" data-testid="le-headline" /></div>
          <div><Label className="text-sm">Short bio * <span className="text-kid-muted">(shown in search)</span></Label><Textarea value={f.short_bio} onChange={(e) => set("short_bio", e.target.value)} rows={2} className="mt-1.5 bg-white" data-testid="le-short-bio" /></div>
          <div><Label className="text-sm">Detailed description</Label><Textarea value={f.detailed_bio} onChange={(e) => set("detailed_bio", e.target.value)} rows={4} className="mt-1.5 bg-white" data-testid="le-detailed-bio" /></div>
          <div><Label className="text-sm">Describe this service</Label><Textarea value={f.service_description} onChange={(e) => set("service_description", e.target.value)} rows={3} className="mt-1.5 bg-white" data-testid="le-service-desc" /></div>
          <div><Label className="text-sm">Your approach</Label><Textarea value={f.approach} onChange={(e) => set("approach", e.target.value)} rows={2} className="mt-1.5 bg-white" data-testid="le-approach" /></div>

          <div>
            <Label className="text-sm">Accepting new clients?</Label>
            <Select value={f.accepting_new_clients} onValueChange={(v) => set("accepting_new_clients", v)}>
              <SelectTrigger className="mt-1.5 bg-white max-w-xs" data-testid="le-accepting"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="waitlist">Waitlist</SelectItem><SelectItem value="no">Not right now</SelectItem><SelectItem value="varies">Varies</SelectItem></SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm block mb-2">Age ranges</Label>
            <Chips options={meta.age_ranges} selected={f.age_ranges} onToggle={(v) => toggleArr("age_ranges", v)} testid="le-ages" />
            <Label className="text-sm block mt-4 mb-2">Support areas</Label>
            <Chips options={meta.support_areas} selected={f.support_areas} onToggle={(v) => toggleArr("support_areas", v)} testid="le-support" />
          </div>

          <div>
            <Label className="text-sm block mb-2">How this service is delivered</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              {DELIVERY_FLAGS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-kid-navy"><Switch checked={!!f.delivery?.[k]} onCheckedChange={(v) => setDelivery(k, v)} data-testid={`le-delivery-${k}`} /> {l}</label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm">Are you a regulated health professional?</Label>
            <Select value={f.credentials?.regulated || "unsure"} onValueChange={(v) => setCred("regulated", v)}>
              <SelectTrigger className="mt-1.5 mb-4 bg-white max-w-xs" data-testid="le-regulated"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem><SelectItem value="unsure">Not sure</SelectItem></SelectContent>
            </Select>
            {f.credentials?.regulated === "yes" && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sm">Regulatory college</Label><Input value={f.credentials?.college || ""} onChange={(e) => setCred("college", e.target.value)} placeholder="e.g. CASLPO, COTO, CPO" className="mt-1.5 bg-white" data-testid="le-college" /></div>
                <div><Label className="text-sm">Registration #</Label><Input value={f.credentials?.registration_number || ""} onChange={(e) => setCred("registration_number", e.target.value)} className="mt-1.5 bg-white" data-testid="le-regnum" /></div>
                <div className="sm:col-span-2"><Label className="text-sm">Public register URL</Label><Input value={f.credentials?.public_register_url || ""} onChange={(e) => setCred("public_register_url", e.target.value)} placeholder="https://" className="mt-1.5 bg-white" data-testid="le-register-url" /></div>
              </div>
            )}
          </div>

          {/* Booking & enquiries */}
          <div>
            <Label className="text-sm block mb-2 font-semibold text-kid-navy">Booking &amp; enquiries</Label>
            <label className="flex items-center gap-2 text-sm text-kid-navy mb-3"><Switch checked={!!f.booking?.accepts_kidpath_requests} onCheckedChange={(v) => setBooking("accepts_kidpath_requests", v)} data-testid="le-accepts-requests" /> Accept booking requests through KidPath</label>
            <div><Label className="text-sm">Booking contact email <span className="text-kid-muted">(where requests are sent)</span></Label><Input value={f.booking?.booking_contact_email || ""} onChange={(e) => setBooking("booking_contact_email", e.target.value)} placeholder="bookings@practice.ca" className="mt-1.5 bg-white" data-testid="le-booking-email" /></div>
            <label className="flex items-center gap-2 text-sm text-kid-navy mt-3 mb-3"><Switch checked={!!f.booking?.external_booking_enabled} onCheckedChange={(v) => setBooking("external_booking_enabled", v)} data-testid="le-external-enabled" /> Use an external booking link (Jane App, Calendly, your site)</label>
            {f.booking?.external_booking_enabled && (
              <div><Label className="text-sm">External booking URL</Label><Input value={f.booking?.external_booking_url || ""} onChange={(e) => setBooking("external_booking_url", e.target.value)} placeholder="https://yourclinic.janeapp.com" className="mt-1.5 bg-white" data-testid="le-external-url" /></div>
            )}
            <div className="grid sm:grid-cols-2 gap-4 mt-3">
              <div><Label className="text-sm">Typical response time</Label>
                <Select value={f.booking?.typical_response_time || "varies"} onValueChange={(v) => setBooking("typical_response_time", v)}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="le-response-time"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="same_day">Same day</SelectItem><SelectItem value="1-2_days">1–2 days</SelectItem><SelectItem value="within_a_week">Within a week</SelectItem><SelectItem value="varies">Varies</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-sm">Consultation cost</Label>
                <Select value={f.booking?.consultation_cost_type || "not_specified"} onValueChange={(v) => setBooking("consultation_cost_type", v)}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="le-cost-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="free">Free consultation</SelectItem><SelectItem value="paid">Paid consultation</SelectItem><SelectItem value="varies">Varies</SelectItem><SelectItem value="not_specified">Not specified</SelectItem></SelectContent>
                </Select></div>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <Switch checked={f.attestation} onCheckedChange={(v) => set("attestation", v)} data-testid="le-attestation" />
            <span className="text-sm text-kid-muted">I confirm the information in this listing is accurate, I'm authorized to represent this practice, and I hold the qualifications and (where applicable) registration stated. *</span>
          </label>

          <div className="flex flex-wrap gap-3 pt-2 border-t border-kid-line sticky bottom-0 bg-white py-3">
            <Button variant="outline" onClick={save} disabled={saving} data-testid="le-save"><Save size={16} className="mr-1.5" /> Save</Button>
            {["draft", "needs_changes", "rejected"].includes(listing.profile_status) && (
              <Button onClick={submit} disabled={saving} data-testid="le-submit"><Send size={16} className="mr-1.5" /> Submit for review</Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function ProviderDashboard() {
  const { user, logout } = useAuth();
  const [meta, setMeta] = useState({ categories: [], cities: [], age_ranges: [], support_areas: [] });
  const [org, setOrg] = useState(null);
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState({});
  const [requests, setRequests] = useState([]);
  const [emailVerified, setEmailVerified] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const [{ data: m }, { data }, { data: st }, { data: rq }] = await Promise.all([
      api.get("/marketplace/meta"),
      api.get("/marketplace/provider/me"),
      api.get("/marketplace/provider/me/stats"),
      api.get("/marketplace/provider/me/booking-requests"),
    ]);
    setMeta(m); setOrg(data.org); setListings(data.listings); setEmailVerified(data.user.email_verified);
    setStats(st); setRequests(rq);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const reloadListings = async () => {
    const [{ data }, { data: st }] = await Promise.all([
      api.get("/marketplace/provider/me"),
      api.get("/marketplace/provider/me/stats"),
    ]);
    setListings(data.listings); setStats(st);
    if (editing) setEditing(data.listings.find((l) => l.id === editing.id) || null);
  };

  const setRequestStatus = async (id, status) => {
    try {
      await api.patch(`/marketplace/provider/me/booking-requests/${id}`, { status });
      const { data: rq } = await api.get("/marketplace/provider/me/booking-requests");
      setRequests(rq);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createListing = async () => {
    if (!newCat) { toast.error("Please choose a service."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/marketplace/provider/me/listings", { primary_category: newCat });
      setAddOpen(false); setNewCat("");
      await reloadListings();
      setEditing(data);
      toast.success("Service listing created — add the details.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const removeListing = async (l) => {
    if (!window.confirm("Delete this service listing? This can't be undone.")) return;
    try { await api.delete(`/marketplace/provider/me/listings/${l.id}`); await reloadListings(); toast.success("Listing deleted."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const catName = (slug) => meta.categories.find((c) => c.slug === slug)?.name || slug;

  if (user && !["provider"].includes(user.role)) return <Navigate to="/providers" replace />;
  if (loading) return <div className="min-h-screen bg-kid-mist grid place-items-center"><Spinner /></div>;

  return (
    <div className="min-h-screen bg-kid-mist">
      <header className="bg-white border-b border-kid-line">
        <div className="max-w-[1000px] mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-kid-navy text-xl">KidPath <span className="text-xs text-kid-teal font-poppins uppercase tracking-widest">Provider</span></Link>
          <button onClick={() => logout().then(() => (window.location.href = "/login"))} className="text-sm text-kid-muted inline-flex items-center gap-1" data-testid="provider-logout"><LogOut size={14} /> Sign out</button>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-5 py-8" data-testid="provider-dashboard">
        {!emailVerified && (
          <div className="rounded-2xl bg-kid-sun/15 border border-kid-sun/40 p-4 mb-5 flex items-center gap-3 text-sm text-[#9a7b1e]" data-testid="verify-email-banner">
            <MailWarning size={18} /> Please verify your email — we sent you a link. Your listings can be reviewed once your email is confirmed.
          </div>
        )}

        <OrgPanel meta={meta} org={org} onSaved={setOrg} />

        {/* Listings */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-kid-navy text-lg">Service listings</h2>
          <Button onClick={() => setAddOpen(true)} data-testid="add-listing-btn"><Plus size={16} className="mr-1.5" /> Add service</Button>
        </div>

        {listings.length === 0 ? (
          <div className="kid-card p-10 text-center text-kid-muted" data-testid="listings-empty">
            No service listings yet. Add your first service (e.g. Speech Therapy) to appear in the directory.
          </div>
        ) : (
          <div className="space-y-3" data-testid="listings-list">
            {listings.map((l) => (
              <div key={l.id} className="kid-card p-5 flex items-center justify-between gap-4" data-testid="listing-row">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={l.profile_status} />
                    <span className="text-xs text-kid-muted">{catName(l.primary_category)}</span>
                  </div>
                  <h3 className="font-semibold text-kid-navy mt-1 truncate">{l.headline || "Untitled service"}</h3>
                  <div className="flex items-center gap-4 mt-1.5 text-xs text-kid-muted" data-testid="listing-stats">
                    <span className="inline-flex items-center gap-1"><Eye size={12} /> {stats[l.id]?.views || 0} views</span>
                    <span className="inline-flex items-center gap-1"><MousePointerClick size={12} /> {stats[l.id]?.clicks || 0} clicks</span>
                    <span className="inline-flex items-center gap-1"><Inbox size={12} /> {stats[l.id]?.requests || 0} requests</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {l.profile_status === "approved" && <Link to={`/providers/${l.slug}`} className="text-kid-teal" title="View public" data-testid="listing-view"><ExternalLink size={16} /></Link>}
                  <Button variant="outline" size="sm" onClick={() => setEditing(l)} data-testid="listing-edit"><Pencil size={14} className="mr-1" /> Edit</Button>
                  <button onClick={() => removeListing(l)} className="text-kid-coral p-2 hover:bg-kid-coral/10 rounded-lg" title="Delete" data-testid="listing-delete"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Booking requests */}
        <div className="flex items-center gap-2 mt-10 mb-4">
          <Inbox size={18} className="text-kid-teal" />
          <h2 className="font-display font-bold text-kid-navy text-lg">Booking requests</h2>
          {requests.filter((r) => r.status === "new").length > 0 && (
            <span className="text-xs font-semibold bg-kid-teal text-white px-2 py-0.5 rounded-full" data-testid="new-requests-badge">{requests.filter((r) => r.status === "new").length} new</span>
          )}
        </div>
        {requests.length === 0 ? (
          <div className="kid-card p-8 text-center text-kid-muted" data-testid="requests-empty">
            No booking requests yet. Families who reach out through KidPath will appear here.
          </div>
        ) : (
          <div className="space-y-3" data-testid="requests-list">
            {requests.map((r) => (
              <div key={r.id} className={`kid-card p-5 ${r.status === "archived" ? "opacity-60" : ""}`} data-testid="request-row">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === "new" ? "bg-kid-sun/20 text-[#9a7b1e]" : r.status === "responded" ? "bg-kid-green/15 text-[#3f9d5c]" : "bg-kid-mist text-kid-muted"}`}>{r.status}</span>
                      <span className="text-xs text-kid-muted">{catName(listings.find((l) => l.id === r.listing_id)?.primary_category)}</span>
                    </div>
                    <h3 className="font-semibold text-kid-navy mt-1">{r.requester_name}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-kid-muted">
                      <a href={`mailto:${r.requester_email}`} className="inline-flex items-center gap-1 text-kid-teal"><Mail size={13} /> {r.requester_email}</a>
                      {r.requester_phone && <a href={`tel:${r.requester_phone}`} className="inline-flex items-center gap-1 text-kid-teal"><Phone size={13} /> {r.requester_phone}</a>}
                      {r.child_age_range && <span>Child age: {r.child_age_range}</span>}
                      <span>Prefers: {r.preferred_contact}</span>
                    </div>
                    {r.message && <p className="text-sm text-kid-navy/90 mt-2 whitespace-pre-wrap bg-kid-mist rounded-xl p-3">{r.message}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.status !== "responded" && <Button variant="outline" size="sm" onClick={() => setRequestStatus(r.id, "responded")} data-testid="request-mark-responded">Mark responded</Button>}
                    {r.status !== "archived" && <Button variant="ghost" size="sm" onClick={() => setRequestStatus(r.id, "archived")} className="text-kid-muted" data-testid="request-archive">Archive</Button>}
                    {r.status === "archived" && <Button variant="ghost" size="sm" onClick={() => setRequestStatus(r.id, "new")} data-testid="request-restore">Restore</Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <ListingEditor meta={meta} listing={editing} open={!!editing} onClose={() => setEditing(null)} onChanged={reloadListings} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-listing-dialog">
          <DialogHeader>
            <DialogTitle>Add a service listing</DialogTitle>
            <DialogDescription>Choose the primary service. You can add details and submit it for review next.</DialogDescription>
          </DialogHeader>
          <Select value={newCat} onValueChange={setNewCat}>
            <SelectTrigger className="bg-white" data-testid="add-listing-category"><SelectValue placeholder="Choose a service" /></SelectTrigger>
            <SelectContent>{meta.categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button onClick={createListing} disabled={creating} data-testid="add-listing-confirm">{creating ? "Creating…" : "Create listing"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
